import * as THREE from 'three';

import { BuildingKind, Catalogue, nameOf } from '../game/buildings/Catalogue';
import { Materials, countOf, formOf, iconOf } from '../game/buildings/Materials';
import { Placed } from '../game/buildings/Placed';
import { Holding, Item } from '../network/Connection';
import { screenPlacer } from './ScreenPoint';

/**
 * What a building is, over the building.
 *
 * HTML above the canvas, for the third time in this client and for the same
 * reason: the game renders into a small buffer scaled up by a whole number, so
 * a line of text drawn inside it comes out as a smear. Names, progress bars and
 * this are interface, not world.
 *
 * **Two audiences in one box.** Anybody gets the name, the level and who put it
 * up — that is what is visible from outside. The store and the production rate
 * belong to the owner, and they are here only because the server sent them,
 * which it only does to the owner. The client is not the thing keeping that
 * promise; it could not be.
 */

export class BuildingTooltip {
  private readonly root = document.createElement('div');
  private shown?: {
    placed: Placed;
    anchor: THREE.Vector3;
    holding?: Holding;
    /** When this client was last told how long was left, by its own clock. */
    since: number;
    /**
     * When the store in `holding` was last heard, by this client's clock.
     *
     * Not the same instant as `since`, and that difference is the whole point:
     * `readyIn` is counted afresh every time the building is looked up, while a
     * store is whatever the last `estate` message said. Restarting the store's
     * count-up on every hover would show the same number again each time the
     * cursor came back — the one thing a clock on this side is meant to avoid.
     */
    heard: number;
  };

  /**
   * What the box currently says, so it is not rebuilt sixty times a second.
   *
   * The countdown is the one part that moves and it moves once a second, so
   * that is the granularity: identity, the holding it was drawn from, and the
   * second being shown. Anything else changing means the same building said
   * something different, and that is a repaint.
   */
  private painted?: { id: string; holding?: Holding; seconds: number; made: number };

  constructor(
    private readonly container: HTMLElement,
    private readonly canvas: HTMLCanvasElement,
    private readonly catalogue: Catalogue,
    private readonly materials: Materials,
    /** Who is looking, so that "yours" can be said rather than your own name. */
    private readonly self: string
  ) {
    this.root.className = 'building-tip';
    this.root.hidden = true;
    this.container.appendChild(this.root);
  }

  /**
   * Point at a building, or at nothing.
   *
   * Rebuilt only when the building under the cursor changes, not every frame:
   * the countdown inside it is the one part that moves, and it is redrawn on
   * its own below.
   */
  show(
    placed: Placed | undefined,
    anchor: THREE.Vector3 | undefined,
    holding?: Holding,
    heard = performance.now()
  ): void {
    if (!placed || !anchor) {
      this.hide();
      return;
    }

    // `since` is reset on every call because `placed.readyIn` arrives freshly
    // counted down each time — the two belong together, and keeping an older
    // start beside a newer remainder would count the same seconds twice.
    // `heard` is not reset, because the store was said once and is filling from
    // then, whatever the cursor does since.
    this.shown = { placed, anchor: anchor.clone(), holding, since: performance.now(), heard };
    this.paint();
    this.root.hidden = false;
  }

  hide(): void {
    if (this.shown) {
      this.shown = undefined;
      this.painted = undefined;
      this.root.hidden = true;
    }
  }

  /**
   * Put it where the building is.
   *
   * Nothing is measured while nothing is shown — a layout read is the expensive
   * half of this, and the common case is a cursor over open ground.
   */
  update(camera: THREE.OrthographicCamera): void {
    const shown = this.shown;
    if (!shown) return;

    // The countdown runs on this client's clock, the way the bar over the
    // building does. Nothing has to tell it the building is finished: the
    // second it reaches zero is the second the box repaints with a store and a
    // rate in it, because those are what a finished building has.
    this.paint();

    const at = screenPlacer(this.container, this.canvas, camera)(shown.anchor);
    if (!at) {
      // Behind the camera, or off the canvas: hidden rather than clamped to an
      // edge, where it would name something that is not under it.
      this.root.hidden = true;
      return;
    }

    this.root.hidden = false;
    this.root.style.left = `${Math.round(at.x)}px`;
    this.root.style.top = `${Math.round(at.y)}px`;
  }

  dispose(): void {
    this.root.remove();
  }

  /** Seconds of building left, by this client's clock. */
  private leftOf(shown: { placed: Placed; since: number }): number {
    return Math.max(0, shown.placed.readyIn - (performance.now() - shown.since) / 1000);
  }

  private paint(): void {
    const shown = this.shown;
    if (!shown) return;

    const left = this.leftOf(shown);
    const seconds = Math.ceil(left);
    const made = madeSince(shown.holding, shown.heard);
    const was = this.painted;
    if (
      was &&
      was.id === shown.placed.id &&
      was.holding === shown.holding &&
      was.seconds === seconds &&
      was.made === made
    ) {
      return;
    }

    this.painted = { id: shown.placed.id, holding: shown.holding, seconds, made };
    this.root.replaceChildren(...this.lines(shown.placed, left, shown.holding, made));
  }

  private lines(placed: Placed, left: number, holding?: Holding, made = 0): HTMLElement[] {
    const kind = this.catalogue.buildings.find((entry) => entry.id === placed.kind);
    const out: HTMLElement[] = [];

    // A kind this build does not know still gets a box, with its number for a
    // name. A tooltip that refuses to open is worse than one that says little:
    // the building is standing there either way.
    //
    // **Osada mówi swoją nazwą.** Rodzaj schodzi wtedy do drugiej linii, razem
    // z poziomem — Białogród jest wioską, ale nazywa się Białogród.
    out.push(row('building-tip-name', nameOf(kind, placed)));
    // Ród pod nazwą — to jest to, co odwiedzający ma o tym miejscu wiedzieć
    // zaraz po tym, jak się nazywa.
    if (placed.house) {
      out.push(row('building-tip-line building-tip-house', `Własność rodu ${placed.house}`));
    }
    // Rodzaj pod nazwą — ale nie wtedy, gdy stoi już **w** nazwie: „Warownia
    // Czarnogród" z podpisem „warownia" mówi to samo dwa razy.
    if (placed.name && kind && !kind.titled) {
      out.push(row('building-tip-line building-tip-dim', kind.label));
    }

    // Only where there is a choice to describe. A camp has one level and
    // "poziom 1 z 1" is a sentence about the catalogue, not about the building.
    if (kind && kind.levels.length > 1) {
      out.push(row('building-tip-line', `poziom ${placed.level} z ${kind.levels.length}`));
    }

    const mine = placed.owner === this.self;
    out.push(row('building-tip-owner', mine ? 'twój' : `postawił: ${placed.ownerName}`));

    // A building site has nothing to say about a store or a rate: it holds
    // nothing and makes nothing, and printing "empty" and "after it is
    // finished" is two lines saying what the third one already said. The box
    // grows those lines the moment the countdown reaches zero.
    if (left > 0) {
      // Words rather than a second bar: one already stands over the building,
      // and two readings of the same number in one place is one too many.
      out.push(row('building-tip-line', `w budowie — ${countdown(left)}`));
      return out;
    }

    if (!mine) return out;

    // Ludność nad produkcją, bo osada jest przede wszystkim ludźmi, a dopiero
    // potem magazynem. Brak liczby znaczy „to nie jest osada" — obóz, chata,
    // kamieniołom — a nie „nikt tu nie mieszka".
    if (holding?.people !== undefined) {
      out.push(row('building-tip-line', `mieszkańcy: ${holding.people}`));
    }

    out.push(this.production(kind, holding));
    out.push(...this.store(holding, made));
    return out;
  }

  /**
   * What it makes, in the server's words.
   *
   * The rate is **not computed here**. It depends on how thick the wood around
   * the building is, the server reads that off the map when it sends this, and
   * a second implementation on this side would be the twin-rule debt this
   * project already carries for movement — taken on for a number small enough
   * to send.
   */
  private production(kind: BuildingKind | undefined, holding?: Holding): HTMLElement {
    if (!holding?.makes?.length || holding.perHour === undefined) {
      // A building that makes nothing is a different thing from one whose rate
      // has not arrived, but both read the same from here and neither is worth
      // a puzzle: the camp is the only one, and it plainly is not a workshop.
      const makes = kind?.levels.some((level) => level.output);
      return row(
        'building-tip-line building-tip-dim',
        makes ? 'produkcja: brak danych' : 'nic nie produkuje'
      );
    }

    // Stawka na surowiec, nie na wyprawę: „1,0 skóry/h i 2,0 sztuk mięsa/h"
    // jest tym, co widać w magazynie, a „1,0 zdobyczy/h" nie jest niczym, co
    // dałoby się policzyć wzrokiem.
    //
    // One decimal, because the ground bonus makes these rarely whole: a hut
    // beside a level-three wood makes 2,6 an hour and rounding it to 3 would be
    // showing a number the server does not use.
    const rates = holding.makes.map((made) => {
      const per = (holding.perHour as number) * made.each;
      const icon = iconOf(this.materials, made.material);
      const name = formOf(this.materials, made.material, Math.round(per));
      return `${icon} ${per.toFixed(1)} ${name}/h`;
    });

    return row('building-tip-line', `produkcja: ${rates.join(', ')}`);
  }

  private store(holding?: Holding, made = 0): HTMLElement[] {
    const items = withMade(holding, made).filter((item) => item.amount > 0);
    if (items.length === 0) {
      // Zeroes are filtered rather than listed, the same way the purse does it:
      // a store empties by going to zero and the row stays behind, so the
      // server knows a material was once there and the player wants to know
      // what is there now.
      return [row('building-tip-line building-tip-dim', 'magazyn: pusty')];
    }

    const out = [row('building-tip-line building-tip-dim', 'magazyn:')];
    for (const item of items) {
      // The ceiling is said only beside what this building makes. A material an
      // administrator dropped in has no ceiling here, and "3 / 48" printed
      // beside it would be a claim about the wrong thing.
      const capacity = holding?.makes?.find((made) => made.material === item.material)?.capacity;
      const ceiling = capacity !== undefined ? ` z ${capacity}` : '';
      out.push(
        row(
          'building-tip-item',
          `${iconOf(this.materials, item.material)} ${countOf(
            this.materials,
            item.material,
            item.amount
          )}${ceiling}`
        )
      );
    }
    return out;
  }
}

/**
 * How many more units have landed since the server last said anything.
 *
 * The store fills on this client's clock, the way the build bar empties on it
 * and the sky turns on it: the server sends a period (`perHour`) and an offset
 * (`nextIn`), and nothing has to be pushed while a hut quietly works. This is
 * the twin of the server's own arithmetic and the only one in this feature —
 * taken on for the same reason the day phase takes it on, and kept to one
 * `floor` so there is little to drift.
 *
 * The ceiling is here too, because a box counting past `capacity` would be
 * promising goods the server has already thrown away.
 */
export function madeSince(holding: Holding | undefined, heard: number): number {
  if (!holding?.perHour || holding.nextIn === undefined) return 0;

  const elapsed = (performance.now() - heard) / 1000;
  if (elapsed < holding.nextIn) return 0;

  const period = 3600 / holding.perHour;
  const made = 1 + Math.floor((elapsed - holding.nextIn) / period);

  // Never past the ceiling. `nextIn` is absent on a full store, so this only
  // bites on one that fills up while it is being watched.
  //
  // Liczone z **najciaśniejszego** składnika, bo zdobyczy nie da się rozbić:
  // magazyn, w którym zostało miejsce na jedno mięso, nie przyjmie wyprawy,
  // choć na skórę miejsca jest dwadzieścia. Bliźniak `room_for` z serwera.
  const room = (holding.makes ?? []).reduce((least, line) => {
    const held = holding.store.find((item) => item.material === line.material)?.amount ?? 0;
    const fits = Math.floor(Math.max(0, line.capacity - held) / line.each);
    return Math.min(least, fits);
  }, Number.POSITIVE_INFINITY);

  if (!Number.isFinite(room)) return made;
  return Math.max(0, Math.min(made, room));
}

/**
 * A store with what has landed since the server spoke counted in.
 *
 * A copy, not an edit: the holding is the last thing the server said, and
 * writing this client's guess into it would make the next comparison see a
 * change that never came over the wire.
 */
export function withMade(holding: Holding | undefined, made: number): Item[] {
  let items = holding?.store ?? [];
  if (!made || !holding?.makes?.length) return items;

  // `made` liczy **sztuki zdobyczy**, więc każdy wiersz dostaje swoje `each`
  // razy tyle: jedna wyprawa myśliwych to jedna skóra i dwa mięsa.
  for (const line of holding.makes) {
    const amount = made * line.each;
    const at = items.findIndex((item) => item.material === line.material);
    items =
      at < 0
        ? [...items, { material: line.material, amount }]
        : items.map((item, index) =>
            index === at ? { ...item, amount: item.amount + amount } : item
          );
  }

  return items;
}

/**
 * Ile zostało, w dniach, godzinach, minutach i sekundach.
 *
 * **Jednostka o wartości zero nie jest pokazywana.** Dwie minuty równo to „2
 * min", nie „0 d 0 godz. 2 min 0 s" — i nie „2 min 0 s", bo zero sekund jest
 * dokładnie tak samo puste jak zero dni. Ostatnia sekunda wypada tym samym
 * sitem, więc pełne minuty i pełne godziny czytają się jak okrągłe liczby,
 * którymi są.
 *
 * Zaokrąglane **w górę**, jak przedtem: dopóki cokolwiek zostało, licznik ma
 * pokazywać co najmniej jedną sekundę, a nie zero nad budynkiem, który jeszcze
 * stoi w rusztowaniu. Ta sama liczba steruje przemalowaniem dymka, więc napis
 * zmienia się dokładnie wtedy, kiedy zmienia się to, co ma pokazać.
 */
export function countdown(seconds: number): string {
  let left = Math.max(0, Math.ceil(seconds));

  const parts: string[] = [];
  for (const [size, name] of [
    [86400, 'd'],
    [3600, 'godz.'],
    [60, 'min'],
    [1, 's'],
  ] as const) {
    const many = Math.floor(left / size);
    left -= many * size;
    if (many > 0) parts.push(`${many} ${name}`);
  }

  // Nothing left at all still needs a word: the line is only drawn while
  // something is, but a caller that rounds to zero should not get an empty box.
  return parts.length > 0 ? parts.join(' ') : '0 s';
}

function row(className: string, text: string): HTMLElement {
  const line = document.createElement('div');
  line.className = className;
  line.textContent = text;
  return line;
}
